/**
 * Consent package 2d: a fresh site-form consent re-opens the channels it covers.
 *
 * Matt 2026-11-04 (first-hand, coordinator session): "Everything, incl. real
 * STOP." A person who fills in a genuine website form and ticks the text box
 * has asked, in writing, to be texted again. That clears their sms holds,
 * including a real STOP reply and the imported TCPA hard stop. The email line
 * does the same for email holds. Nothing else moves:
 *
 *   - A call hold never clears (do-not-call, the DNC registry scrub, any
 *     call-channel row). A do-not-call row on the sms channel (the BatchData
 *     DNC scrub writes both) stays too: it is a registry flag, not a consent
 *     choice the form answers.
 *   - An 'all'-channel row is cleared only after replacement rows with the same
 *     reason and source hold every channel the form did not cover. Calls are
 *     always replaced, so calls stay held.
 *   - Bounces, complaints, broker flags (counsel, represented, wrong party),
 *     GPC / privacy choices and test fences are not on the allow-list and stay.
 *
 * The switch FORM_REOPTIN_LIFTS_SUPPRESSION defaults OFF. Turning it on needs
 * Matt's own yes. With it off nothing here runs and every form behaves exactly
 * as before.
 *
 * Twilio keeps its own STOP list (error 21610) until the number texts START.
 * Clearing our row does not change that and nothing here tries to get around
 * it: a send that comes back 21610 re-suppresses sms (lib/crm/twilio.ts).
 *
 * Pure: no I/O. lib/data/crm/applyFormReoptin.ts does the writes.
 */

import { isProtectedComplianceTag } from '@/lib/crm/bulk-handlers/protected-tags'

export const FORM_REOPTIN_ENV = 'FORM_REOPTIN_LIFTS_SUPPRESSION' as const

/** On only for true / 1 / on / yes (any case, trimmed). Unset or anything else is off. */
export function formReoptinEnabled(env: Record<string, string | undefined> = process.env): boolean {
  const raw = (env[FORM_REOPTIN_ENV] ?? '').trim().toLowerCase()
  return raw === 'true' || raw === '1' || raw === 'on' || raw === 'yes'
}

/**
 * The site-form consent wording version. Bumped from the text-only wording
 * because the forms now carry the email line below.
 */
export const FORM_CONSENT_WORDING_VERSION = 'site-form-consent-v2-2026-11-04' as const

/** The email consent sentence shown under the text box on the site forms. Matt reads public copy. */
export const EMAIL_CONSENT_TEXT =
  'I also agree to receive emails from Ryan Realty about my request, including listings, home-value updates and market reports. I can unsubscribe at any time.'

/** Proof rows go to sarah_consent_events with this source. */
export const FORM_REOPTIN_PROOF_SOURCE = 'site-form' as const

export type FormReoptinChannel = 'sms' | 'email'
export type SuppressionChannel = 'all' | 'sms' | 'email' | 'call'

export type FormReoptinSuppression = {
  id: number
  channel: string
  reason: string
  source: string | null
}

export type FormReoptinConsent = { sms: boolean; email: boolean }

export type FormReoptinReplacement = {
  /** The 'all' row this stands in for. */
  forId: number
  channel: Exclude<SuppressionChannel, 'all'>
  reason: string
  source: string | null
}

export type FormReoptinPlan = {
  /** Rows to stamp cleared. */
  clearIds: number[]
  /** Rows to write BEFORE any clear (never remove-then-add). */
  replacements: FormReoptinReplacement[]
  /** Channels that end up with at least one row cleared; one proof row each. */
  clearedChannels: FormReoptinChannel[]
  /** Rows left alone, for the timeline note. */
  keptIds: number[]
}

function normReason(reason: string): string {
  return reason.trim().toLowerCase().replace(/^contact:/, '')
}

/** Never lifted by a form, on any channel. */
function isNeverLifted(reason: string): boolean {
  const r = normReason(reason)
  return r.includes('do-not-call') || r.includes('dnc')
}

const SMS_LIFTABLE = new Set(['stop-keyword', 'twilio-opt-out', 'tcpa-hard-stop', 'no-sms-consent', 'do-not-text'])
const EMAIL_LIFTABLE_EXACT = new Set(['do-not-email', 'unsubscribe', 'unsubscribed', 'tcpa-hard-stop'])

/** Can a ticked form box lift this reason on this channel? */
export function isLiftableFor(channel: FormReoptinChannel, reason: string): boolean {
  if (isNeverLifted(reason)) return false
  const r = normReason(reason)
  if (channel === 'sms') return SMS_LIFTABLE.has(r)
  return EMAIL_LIFTABLE_EXACT.has(r) || r.startsWith('do-not-email:')
}

export function planFormReoptinLift(
  rows: readonly FormReoptinSuppression[],
  consent: FormReoptinConsent,
): FormReoptinPlan {
  const clearIds: number[] = []
  const keptIds: number[] = []
  const replacements: FormReoptinReplacement[] = []
  const cleared = new Set<FormReoptinChannel>()

  for (const row of rows) {
    const channel = row.channel.trim().toLowerCase()
    if (channel === 'sms' || channel === 'email') {
      if (consent[channel] && isLiftableFor(channel, row.reason)) {
        clearIds.push(row.id)
        cleared.add(channel)
      } else {
        keptIds.push(row.id)
      }
      continue
    }
    if (channel === 'all') {
      const covered = (['sms', 'email'] as const).filter((c) => consent[c] && isLiftableFor(c, row.reason))
      if (covered.length === 0) {
        keptIds.push(row.id)
        continue
      }
      // Hold every channel the form did not cover. Calls are never covered.
      for (const c of ['sms', 'email', 'call'] as const) {
        if (c !== 'call' && covered.includes(c)) continue
        replacements.push({ forId: row.id, channel: c, reason: row.reason, source: row.source })
      }
      clearIds.push(row.id)
      for (const c of covered) cleared.add(c)
      continue
    }
    // 'call' and anything unknown stay.
    keptIds.push(row.id)
  }

  const clearedChannels = (['sms', 'email'] as const).filter((c) => cleared.has(c))
  return { clearIds, replacements, clearedChannels, keptIds }
}

// ---------------------------------------------------------------------------
// Tags. Coordinator 2026-11-04 (on Matt's "Everything, incl. real STOP"): the
// send-time check also reads suppression tags (lib/crm/tag-channel.ts), so a
// ticked box removes the tags for that channel too. Conditions:
//   - only boxes ticked on this submit count; an unticked channel's tags stay;
//   - sms removes contact:do-not-text / do_not_text, email removes
//     do_not_email / unsubscribed;
//   - compliance:hard-stop comes off only after a call-channel do-not-call row
//     and a row for every unticked channel are written and confirmed. If any
//     of those writes fails the tag stays (fail closed);
//   - contact:do-not-call, compliance:dnc-registry, bounced and complained
//     never move;
//   - every tag removed must be a protected compliance tag
//     (lib/crm/bulk-handlers/protected-tags.ts). This is the single-record,
//     proof-backed path; the bulk handlers still refuse every protected tag.
// ---------------------------------------------------------------------------


export const HARD_STOP_TAG = 'compliance:hard-stop' as const

const TAGS_LIFTED_BY: Readonly<Record<FormReoptinChannel, ReadonlySet<string>>> = {
  sms: new Set(['contact:do-not-text', 'do_not_text']),
  email: new Set(['do_not_email', 'unsubscribed']),
}

/** Never removed by a form, whatever is ticked. */
export const TAGS_NEVER_LIFTED: ReadonlySet<string> = new Set([
  'contact:do-not-call',
  'compliance:dnc-registry',
  'bounced',
  'complained',
])

export type FormReoptinTagReplacement = {
  channel: Exclude<SuppressionChannel, 'all'>
  reason: string
  source: string
}

export type FormReoptinTagPlan = {
  /** Tags to remove, exactly as stored on the person. */
  removeTags: string[]
  /** Channels a removed tag re-opens; each needs a proof row. */
  channels: FormReoptinChannel[]
  /** True when compliance:hard-stop is among removeTags. */
  hardStop: boolean
  /** Rows that must be written and confirmed before compliance:hard-stop comes off. */
  hardStopReplacements: FormReoptinTagReplacement[]
}

export const HARD_STOP_TAG_REPLACEMENT_SOURCE = 'form-reoptin-hard-stop-tag' as const

export function planFormReoptinTagLift(tags: readonly unknown[] | null | undefined, consent: FormReoptinConsent): FormReoptinTagPlan {
  const removeTags: string[] = []
  const channels = new Set<FormReoptinChannel>()
  let hardStop = false
  const anyTicked = consent.sms || consent.email

  for (const raw of tags ?? []) {
    if (typeof raw !== 'string') continue
    const t = raw.trim().toLowerCase()
    if (TAGS_NEVER_LIFTED.has(t)) continue
    if (!isProtectedComplianceTag(t)) continue
    if (t === HARD_STOP_TAG) {
      if (anyTicked) {
        removeTags.push(raw)
        hardStop = true
        for (const c of ['sms', 'email'] as const) if (consent[c]) channels.add(c)
      }
      continue
    }
    for (const c of ['sms', 'email'] as const) {
      if (consent[c] && TAGS_LIFTED_BY[c].has(t)) {
        removeTags.push(raw)
        channels.add(c)
      }
    }
  }

  const hardStopReplacements: FormReoptinTagReplacement[] = []
  if (hardStop) {
    hardStopReplacements.push({ channel: 'call', reason: 'do-not-call', source: HARD_STOP_TAG_REPLACEMENT_SOURCE })
    for (const c of ['sms', 'email'] as const) {
      if (!consent[c]) hardStopReplacements.push({ channel: c, reason: 'tcpa-hard-stop', source: HARD_STOP_TAG_REPLACEMENT_SOURCE })
    }
  }

  return {
    removeTags,
    channels: (['sms', 'email'] as const).filter((c) => channels.has(c)),
    hardStop,
    hardStopReplacements,
  }
}
